import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type { AttentionItem, MyWork } from '../lib/types';
import {
  ATTENTION_TONE,
  IMPORTANCE_TONE,
  formatDate,
  overdueLabel,
  relativeDays,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Loading,
  PageHeader,
  StatTile,
  cn,
} from '../components/ui';
import { WorkItemDialog } from '../features/WorkItemDialog';

/**
 * §21 CSM Daily Workspace.
 *
 * Answers §69's question for a CSM: *what must I do today so that nothing I
 * promised or need to follow up on gets forgotten?*
 *
 * The ordering is the server's — it comes from §21's priority list, computed on
 * each account's own business calendar. The screen's job is to make that order
 * legible: the most urgent group first, each row saying what it is and why it is
 * on the list. There is no "sort by" control on the primary list, deliberately:
 * the whole value is that someone else decided the order.
 */
export function MyWorkPage() {
  const { me } = useSession();
  const [scope, setScope] = useState<'mine' | 'accounts'>('mine');
  const [openWorkItemId, setOpenWorkItemId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['my-work', scope],
    queryFn: () => api.get<MyWork>('/cs/my-work', { scope }),
  });

  const data = query.data;
  const counts = summarise(data?.items ?? []);

  return (
    <>
      <PageHeader
        title="My Work"
        description={
          scope === 'mine'
            ? 'Everything you own that needs attention, ordered by urgency.'
            : 'Everything that needs attention across the accounts you cover, whoever owns it.'
        }
        actions={
          <div className="flex rounded-md border border-border-strong p-0.5">
            {(['mine', 'accounts'] as const).map((option) => (
              <button
                key={option}
                onClick={() => setScope(option)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  scope === option ? 'bg-accent-soft text-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                {option === 'mine' ? 'Mine' : 'My accounts'}
              </button>
            ))}
          </div>
        }
      />

      {query.isLoading && <Loading label="Assembling your day" />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {data && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile
              label="Overdue commitments"
              value={counts.overdue}
              tone="critical"
              context="Past the customer date"
            />
            <StatTile
              label="At risk"
              value={counts.atRisk}
              tone="serious"
              context="Blocked or past internal target"
            />
            <StatTile
              label="Readiness checks due"
              value={counts.readiness}
              tone="warning"
              context="Confirm before the customer date"
            />
            <StatTile
              label="Follow-ups needing action"
              value={counts.followups}
              tone="serious"
              context="Overdue or response detected"
            />
            <StatTile
              label="Customers to notify"
              value={counts.notifications}
              tone="info"
              context="Requested features that shipped"
            />
          </div>

          {data.total === 0 ? (
            <EmptyState
              title="Nothing needs your attention today"
              description={
                scope === 'mine'
                  ? 'No overdue commitments, no follow-ups due, no readiness checks outstanding. Switch to “My accounts” to see work owned by others on accounts you cover.'
                  : 'Nothing outstanding across the accounts you cover.'
              }
            />
          ) : (
            <div className="space-y-4">
              {data.sections.map((section) => (
                <Card key={section.kind} padded={false}>
                  <div className="px-4 pt-4">
                    <CardHeader
                      title={
                        <span className="flex items-center gap-2">
                          <Badge tone={ATTENTION_TONE[section.kind] ?? 'neutral'} dot>
                            {section.label}
                          </Badge>
                        </span>
                      }
                      count={section.count}
                      description={SECTION_HELP[section.kind]}
                    />
                  </div>
                  <ul className="divide-y divide-border border-t border-border">
                    {section.items.map((item) => (
                      <AttentionRow
                        key={`${item.kind}-${item.workItemId ?? item.accountId}`}
                        item={item}
                        onOpen={() => item.workItemId && setOpenWorkItemId(item.workItemId)}
                      />
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          )}

          <p className="mt-6 text-xs text-muted">
            Ordered by the §21 priority list. Generated {formatDate(data.generatedAt)} for{' '}
            {me.fullName}. Response-SLA breaches and AI proposals join this list once email
            ingestion (Phase 4) and the proposal framework (Phase 3) are connected.
          </p>
        </>
      )}

      {openWorkItemId && (
        <WorkItemDialog
          workItemId={openWorkItemId}
          onClose={() => setOpenWorkItemId(null)}
          onChanged={() => query.refetch()}
        />
      )}
    </>
  );
}

function AttentionRow({ item, onOpen }: { item: AttentionItem; onOpen: () => void }) {
  const tone = ATTENTION_TONE[item.kind] ?? 'neutral';
  const overdue = (item.businessDaysOverdue ?? 0) > 0;

  return (
    <li>
      <button
        onClick={onOpen}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-ink">{item.title}</span>
            {item.accountImportance === 'strategic' && (
              <Badge tone={IMPORTANCE_TONE.strategic} dot={false}>
                Strategic
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2">
            <Link
              to={`/accounts/${item.accountId}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium text-accent hover:underline"
            >
              {item.accountName}
            </Link>
            <span className="text-muted">·</span>
            <span>{item.reason}</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone={overdue ? tone : 'neutral'} dot={overdue}>
            {overdueLabel(item.businessDaysOverdue)}
          </Badge>
          <span className="text-xs text-muted">
            {item.dueAt ? `${formatDate(item.dueAt)} · ${relativeDays(item.dueAt)}` : 'No date'}
          </span>
        </div>
      </button>
    </li>
  );
}

const SECTION_HELP: Record<string, string> = {
  critical_overdue_commitment:
    'Critical promises past their customer date. These are visible to leadership.',
  commitment_at_risk:
    'Past the customer date, blocked by a dependency, or past the internal target date.',
  customer_notification:
    'This account asked for a feature that has since shipped. Closing the loop is the whole point of §39.',
  internal_readiness_check:
    'The internal check date has arrived. Confirm the commitment will actually land.',
  followup_overdue: 'You are waiting on the customer and the follow-up date has passed.',
  customer_response_detected:
    'A customer message appears to answer an outstanding request. Review it and close the follow-up — nothing closes automatically.',
  commitment_due_soon: 'Coming up inside the next week of working days.',
  routine_task: 'Tasks due soon.',
};

function summarise(items: AttentionItem[]) {
  return {
    overdue: items.filter(
      (i) => i.kind === 'critical_overdue_commitment' || (i.kind === 'commitment_at_risk' && (i.businessDaysOverdue ?? 0) > 0),
    ).length,
    atRisk: items.filter((i) => i.kind === 'commitment_at_risk' && (i.businessDaysOverdue ?? 0) <= 0)
      .length,
    readiness: items.filter((i) => i.kind === 'internal_readiness_check').length,
    followups: items.filter(
      (i) => i.kind === 'followup_overdue' || i.kind === 'customer_response_detected',
    ).length,
    notifications: items.filter((i) => i.kind === 'customer_notification').length,
  };
}

export { titleCase };
